import { ReactElement, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { getPolygonNormal } from '@scene'
import { Vector3 } from 'three'
import { TabContext, TabPanel, TabList } from '@mui/lab'
import { Stack, styled } from '@mui/material'
import { ImmutableVector3, getCenterMultipleReduceMean } from '@modugen/ui-toolkit'
import { MagicButton } from '@ui/actions'
import { FormTab } from '@ui/forms'
import { useModelStore } from '@editorStores'
import { useSceneElement, useTypeInteraction } from '@editorHooks'
import Text3DThree from 'src/components/scene/Text3DThree'
import {
  createAutoDetermineAssignmentStrategy,
  type RoofAssignmentFormValue,
} from './assignmentStrategies'
import { RoofFormFields } from './components'
import { tabConfig, windAngleToDirection } from './constants'
import { configToLabel } from './utils'

const StyledTabPanel = styled(TabPanel)({
  padding: 0,
})

interface RoofAssignmentFormValues {
  roofAssignment: RoofAssignmentFormValue
}

interface RoofAssignmentMarkerEntry {
  id: string
  lines: string[]
  position: { x: number; y: number; z: number }
  textXOrientation: Vector3
  textYOrientation: Vector3
}

const RoofAssignmentSceneText = ({
  entry,
}: {
  entry: RoofAssignmentMarkerEntry
}): ReactElement | null => {
  const textProperties = useMemo(
    () => ({
      textLines: entry.lines,
      color: 'black',
      backgroundColor: '#ffffff',
      borderColor: '#000000',
      borderWidth: 0.025,
      position: new Vector3(entry.position.x, entry.position.y, entry.position.z),
      scale: 0.125,
      textDepth: 0.001,
      textXOrientation: entry.textXOrientation,
      textYOrientation: entry.textYOrientation,
      anchorLocation: 'center' as const,
    }),
    [entry],
  )

  useSceneElement({
    enabled: true,
    component: Text3DThree,
    properties: textProperties,
  })

  return null
}

const RoofAssignment = ({
  setWindAngle,
  activeWindAngle,
}: WallAssignmentParamsActions): ReactElement => {
  const roofSlabs = useModelStore(state => state.model.roof_slabs)
  const { getValues, setValue, formState, trigger, watch } =
    useFormContext<RoofAssignmentFormValues>()
  const defaultValue = tabConfig[0]
  const activeAngle = activeWindAngle || defaultValue
  const activeDirection = windAngleToDirection[activeAngle]
  const activeConfigurationsWatch = watch(`roofAssignment.${activeDirection}.configurations`) as
    | RoofConfig[]
    | undefined
  const activeConfigurations = useMemo(
    () => activeConfigurationsWatch || [],
    [activeConfigurationsWatch],
  )

  const navigateTab = (value: string) => {
    // set to null to remove query param
    setWindAngle(value === defaultValue ? null : (value as WindAngle))
  }

  useEffect(() => {
    setWindAngle(activeWindAngle || defaultValue)
  }, [activeWindAngle, defaultValue, setWindAngle])

  useTypeInteraction('roof_slabs')

  const handleAutoDetermine = () => {
    const nextRoofAssignment = createAutoDetermineAssignmentStrategy({
      roofSlabs,
      currentRoofAssignment: getValues('roofAssignment'),
    })

    setValue('roofAssignment', nextRoofAssignment)

    if (formState.submitCount) trigger('roofAssignment')
  }

  const sceneEntries = useMemo((): RoofAssignmentMarkerEntry[] => {
    const roofSlabByGuid = new Map(roofSlabs.map(roofSlab => [roofSlab.guid, roofSlab]))
    const eX = new ImmutableVector3(1, 0, 0)
    const eZ = new ImmutableVector3(0, 0, 1)

    return activeConfigurations
      .map(configuration => {
        const roofSlab = roofSlabByGuid.get(configuration.element_guid)
        if (!roofSlab?.shape?.points?.length) return null

        const center = getCenterMultipleReduceMean(roofSlab.shape.points).addScaledVector(eZ, 0.01)
        const roofSlabNormal = getPolygonNormal(
          roofSlab.shape.points.map(point => new ImmutableVector3(point.x, point.y, point.z)),
        )
        const yDirectionInPlane = new ImmutableVector3()
          .crossVectors(roofSlabNormal, eX)
          .normalize()
        const safeYDirectionInPlane =
          yDirectionInPlane.length() > 0 ? yDirectionInPlane : new ImmutableVector3(0, 1, 0)
        const xDirectionInPlane = new ImmutableVector3()
          .crossVectors(yDirectionInPlane, roofSlabNormal)
          .normalize()
        const safeXDirectionInPlane =
          xDirectionInPlane.length() > 0 ? xDirectionInPlane : new ImmutableVector3(1, 0, 0)

        const label = configToLabel(configuration)
        // todo implement makeMultiLineLabel
        // split the label by the " - " character and remove leading and trailing whitespace for each line
        const lines = label.split(' - ').map(line => line.trim())
        return {
          id: `${activeDirection}-${configuration.element_guid}`,
          lines,
          position: { x: center.x, y: center.y, z: center.z },
          textXOrientation: new Vector3(
            safeXDirectionInPlane.x,
            safeXDirectionInPlane.y,
            safeXDirectionInPlane.z,
          ),
          textYOrientation: new Vector3(
            safeYDirectionInPlane.x,
            safeYDirectionInPlane.y,
            safeYDirectionInPlane.z,
          ),
        }
      })
      .filter((entry): entry is RoofAssignmentMarkerEntry => !!entry)
  }, [activeConfigurations, activeDirection, roofSlabs])

  return (
    <Stack spacing={1} direction="column">
      {sceneEntries.map(entry => (
        <RoofAssignmentSceneText key={`text-${entry.id}`} entry={entry} />
      ))}
      <MagicButton onClick={handleAutoDetermine} fullWidth data-cy="roof-auto-determine-btn">
        Dachflächen automatisch zuweisen
      </MagicButton>
      <TabContext value={activeAngle}>
        <TabList onChange={(_, value) => navigateTab(value)}>
          {tabConfig.map(angle => (
            <FormTab
              fields={`roofAssignment[${windAngleToDirection[angle]}].configurations`}
              value={angle}
              label={`${angle}°`}
              key={angle}
              data-cy={`tab-angle-${angle}`}
            />
          ))}
        </TabList>
        {tabConfig.map(angle => (
          <StyledTabPanel value={angle} key={angle}>
            <RoofFormFields direction={windAngleToDirection[angle]} />
          </StyledTabPanel>
        ))}
      </TabContext>
    </Stack>
  )
}

export default RoofAssignment
